import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Users, Kanban, Bot, LogOut, Sparkles, ImagePlus, ClipboardList, Zap, AlertOctagon, FileText, Share2, Settings as SettingsIcon, Video, Newspaper, Mail, Bell, HelpCircle, Plug, Map, Rocket } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import ProjectSwitcher from "./ProjectSwitcher";
import GuidedTour, { tourWasOffered } from "./GuidedTour";
import { useProject } from "../context/ProjectContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true,
    help: "Deine Startseite: die wichtigsten Zahlen deines Projekts auf einen Blick." },
  { to: "/produkte", label: "Produkte", icon: Package, testid: "nav-produkte-link",
    help: "Dein Produktkatalog. Der KI-Agent nutzt diese Daten für Werbung, Videos und Seiten." },
  { to: "/leads", label: "Leads", icon: Users, testid: "nav-leads-link",
    help: "Deine Interessenten. Anmeldungen von Landingpages landen automatisch hier." },
  { to: "/pipeline", label: "Pipeline", icon: Kanban, testid: "nav-pipeline-link",
    help: "Dein Verkaufs-Board: Deals per Ziehen zwischen den Phasen bewegen." },
  { to: "/ki-agent", label: "KI-Agent", icon: Bot, testid: "nav-ki-agent-link",
    help: "Dein Chat mit zwei KI-Modellen. Sag einfach, was du brauchst — Aktionen immer mit deiner Freigabe." },
  { to: "/bild-studio", label: "Bild-Studio", icon: ImagePlus, testid: "nav-bild-studio-link",
    help: "Werbebilder per KI erstellen, eigene Bilder und Videos hochladen und zuschneiden." },
  { to: "/skills", label: "Skills", icon: Zap, testid: "nav-skills-link",
    help: "Automatische Aufgaben mit Zeitplan — laufen auch ohne dich im Hintergrund." },
  { to: "/seiten", label: "Seiten", icon: FileText, testid: "nav-seiten-link",
    help: "Landingpages bauen und veröffentlichen. Anmeldungen werden automatisch zu Leads." },
  { to: "/social", label: "Social", icon: Share2, testid: "nav-social-link",
    help: "Beiträge erstellen, plattformoptimierte Texte per KI, zeitplanen und veröffentlichen." },
  { to: "/video-studio", label: "Video", icon: Video, testid: "nav-video-link",
    help: "Produktvideos mit Skript, Erzählstimme und Untertiteln automatisch bauen lassen." },
  { to: "/berichte", label: "Berichte", icon: Newspaper, testid: "nav-berichte-link",
    help: "Automatisch zusammengestellte Rechercheergebnisse und Vorschläge." },
  { to: "/digest", label: "Wochen-Digest", icon: Mail, testid: "nav-digest-link",
    help: "Vorschau deiner Wochen-Zusammenfassung per E-Mail." },
  { to: "/verbindungen", label: "Verbindungen", icon: Plug, testid: "nav-verbindungen-link",
    help: "Alle externen Dienste (Social, SMTP, Shopify) mit ehrlichem Status auf einen Blick." },
  { to: "/einstellungen", label: "Einstellungen", icon: SettingsIcon, testid: "nav-einstellungen-link",
    help: "Not-Stopp, Ausgaben-Limits, Web-Zugriff, Verbindungen und E-Mail." },
  { to: "/protokoll", label: "Protokoll", icon: ClipboardList, testid: "nav-protokoll-link",
    help: "Lückenlose Transparenz: was der Agent wann getan hat und was du freigegeben hast." },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { projects, loading: projectsLoading } = useProject();
  const nav = useNavigate();
  const [emergency, setEmergency] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifType, setNotifType] = useState("");
  const [tourOpen, setTourOpen] = useState(false);

  const loadEmergency = async () => {
    try {
      const { data } = await api.get("/settings/emergency-stop");
      setEmergency(!!data.active);
    } catch { /* ignore */ }
  };
  const loadNotifs = async (typeFilter = notifType) => {
    try {
      const listUrl = typeFilter
        ? `/notifications?limit=20&type=${typeFilter}`
        : "/notifications?limit=20";
      const [{ data: c }, { data: lst }] = await Promise.all([
        api.get("/notifications/unread-count").catch(() => ({ data: { unread: 0 } })),
        api.get(listUrl).catch(() => ({ data: { notifications: [] } })),
      ]);
      setUnread(c.unread || 0);
      setNotifs(lst.notifications || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    loadEmergency();
    loadNotifs();
    const t = setInterval(() => { loadEmergency(); loadNotifs(); }, 30000);
    return () => clearInterval(t);
  }, []);

  // Phase 18 — auto-offer tour once when user has 0 projects after projects load
  useEffect(() => {
    if (projectsLoading) return;
    if (tourWasOffered()) return;
    if ((projects || []).length === 0) {
      setTourOpen(true);
    }
  }, [projectsLoading, projects]);

  const clickNotif = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    setNotifOpen(false);
    loadNotifs();
    if (n.link) nav(n.link);
  };
  const readAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    loadNotifs();
  };

  const toggleEmergency = async () => {
    try {
      const { data } = await api.post("/settings/emergency-stop/toggle");
      setEmergency(!!data.active);
      toast(data.active ? "Not-Stopp aktiviert" : "Not-Stopp deaktiviert");
    } catch (e) {
      toast.error("Not-Stopp konnte nicht umgeschaltet werden");
    }
  };

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-[#0b0f17] text-slate-900 dark:text-slate-100">
      {/* Sidebar */}
      <aside
        className="hidden md:flex md:w-64 flex-col bg-[#0B0F17] border-r border-white/5 text-slate-300"
        data-testid="sidebar"
      >
        <div className="h-16 px-6 flex items-center gap-2 border-b border-white/5" data-testid="sidebar-brand">
          <div className="h-9 w-9 rounded-xl bg-emerald-500/15 text-emerald-400 grid place-items-center">
            <Rocket className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold text-white tracking-tight">Marketing Rocket</div>
            <div className="text-[11px] text-slate-500">Multi-Projekt</div>
          </div>
        </div>

        <ProjectSwitcher />

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid, end, help }) => (
            <div key={to} className="group flex items-center">
              <NavLink
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition
                 ${isActive
                   ? "bg-[#1E293B] text-emerald-400"
                   : "text-slate-400 hover:text-white hover:bg-white/5"}`
                }
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </NavLink>
              <div className="relative">
                <button
                  type="button"
                  data-testid={`${testid}-help`}
                  aria-label={`Hilfe zu ${label}`}
                  className="peer ml-1 h-6 w-6 rounded-full text-slate-600 hover:text-emerald-300 hover:bg-white/5 flex items-center justify-center transition"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                </button>
                <div
                  role="tooltip"
                  data-testid={`${testid}-help-tooltip`}
                  className="hidden peer-hover:block peer-focus:block absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-72 rounded-lg bg-[#111827] border border-emerald-500/30 shadow-xl p-3 text-[11px] text-slate-200 leading-relaxed pointer-events-none"
                >
                  <div className="font-semibold text-white mb-1">{label}</div>
                  {help}
                </div>
              </div>
            </div>
          ))}

          <div
            className="hidden items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-500 cursor-not-allowed opacity-70"
            data-testid="nav-ai-agent-disabled"
            aria-disabled="true"
            title="KI-Agent"
          >
            <Bot className="h-4 w-4" />
            <span>KI-Agent</span>
            <Badge className="ml-auto bg-indigo-500/20 text-indigo-300 border-0 text-[10px]">bald</Badge>
          </div>
        </nav>

        <div className="p-4 border-t border-white/5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTourOpen(true)}
            data-testid="start-tour-button"
            className="w-full mb-2 text-slate-300 hover:text-emerald-300 hover:bg-white/5"
          >
            <Map className="h-4 w-4 mr-2" /> Rundgang starten
          </Button>
          <div className="text-xs text-slate-500 mb-2">Angemeldet als</div>
          <div className="text-sm text-white truncate" data-testid="sidebar-user-name">{user?.name || "—"}</div>
          <div className="text-xs text-slate-500 truncate mb-3" data-testid="sidebar-user-email">{user?.email || ""}</div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full border-white/10 bg-transparent text-slate-300 hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-4 w-4 mr-2" /> Abmelden
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-[#0B0F17] text-white border-b border-white/5 h-14 flex items-center px-4 justify-between">
        <div className="flex items-center gap-2">
          <Rocket className="h-5 w-5 text-emerald-400" />
          <span className="font-bold">Marketing Rocket</span>
        </div>
        <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="logout-button-mobile" className="text-slate-300">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      {/* Main */}
      <main className="flex-1 md:ml-0 pt-14 md:pt-0">
        {emergency && (
          <div className="bg-red-600/90 text-white px-4 py-2 flex items-center justify-between gap-3 border-b border-red-500"
            data-testid="emergency-banner">
            <div className="flex items-center gap-2 text-sm">
              <AlertOctagon className="h-4 w-4" />
              <span className="font-semibold">Not-Stopp aktiv</span>
              <span className="opacity-80 hidden sm:inline">— Alle geplanten Skills sind pausiert.</span>
            </div>
            <Button size="sm" variant="outline" onClick={toggleEmergency}
              data-testid="emergency-stop-toggle"
              className="h-7 border-white/30 bg-transparent text-white hover:bg-white/10">
              Deaktivieren
            </Button>
          </div>
        )}
        {!emergency && (
          <div className="hidden md:flex items-center justify-end gap-3 px-6 py-2 bg-[#0b0f17] border-b border-white/5">
            <div className="relative">
              <button onClick={() => setNotifOpen((v) => !v)}
                data-testid="notifications-bell"
                className="relative h-7 w-7 rounded hover:bg-white/5 text-slate-400 hover:text-white flex items-center justify-center">
                <Bell className="h-4 w-4" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
                    data-testid="notifications-unread-badge">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-9 z-40 w-80 rounded-lg border border-white/10 bg-[#0f172a] shadow-xl"
                  data-testid="notifications-dropdown">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                    <div className="text-xs font-semibold text-white">Benachrichtigungen</div>
                    {notifs.length > 0 && (
                      <button onClick={readAll} data-testid="notifications-read-all"
                        className="text-[11px] text-slate-400 hover:text-emerald-300">
                        Alle als gelesen markieren
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1 px-3 py-1.5 border-b border-white/5"
                    data-testid="notifications-type-filter">
                    {[
                      { k: "", l: "Alle" },
                      { k: "skill_failed", l: "Skills" },
                      { k: "digest_failed", l: "Digest" },
                      { k: "publish_failed", l: "Posts" },
                    ].map(opt => (
                      <button key={opt.k || "all"}
                        onClick={() => { setNotifType(opt.k); loadNotifs(opt.k); }}
                        aria-pressed={notifType === opt.k}
                        data-testid={`notifications-type-${opt.k || "all"}`}
                        className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                          notifType === opt.k
                            ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                            : "border-white/10 bg-white/[0.02] text-slate-400 hover:bg-white/5"
                        }`}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.length === 0 ? (
                      <div className="px-3 py-6 text-center text-[11px] text-slate-500" data-testid="notifications-empty">
                        Keine Benachrichtigungen.
                      </div>
                    ) : notifs.map((n) => (
                      <button key={n.id} onClick={() => clickNotif(n)}
                        data-testid={`notification-item-${n.id}`}
                        className={`w-full text-left px-3 py-2 border-b border-white/5 hover:bg-white/5 ${
                          n.read ? "opacity-60" : ""
                        }`}>
                        <div className="flex items-start gap-2">
                          <div className={`mt-1 h-1.5 w-1.5 rounded-full ${n.read ? "bg-slate-600" : "bg-rose-400"}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-white truncate">
                              {n.title}
                              {n.count > 1 && (
                                <span className="ml-1 text-emerald-400 text-[10px]" data-testid={`notification-count-${n.id}`}>· {n.count}×</span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400 line-clamp-2">{n.body}</div>
                            <div className="text-[10px] text-slate-600 mt-0.5">
                              {n.last_failed_at && new Date(n.last_failed_at).toLocaleString("de-DE")}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={toggleEmergency}
              data-testid="emergency-stop-toggle"
              className="h-7 text-xs text-slate-400 hover:text-red-300">
              <AlertOctagon className="h-3.5 w-3.5 mr-1" /> Not-Stopp
            </Button>
          </div>
        )}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0B0F17] border-t border-white/5 grid grid-cols-[repeat(13,minmax(0,1fr))] overflow-x-auto">
          {NAV.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2 text-[11px] ${
                  isActive ? "text-emerald-400" : "text-slate-400"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>
        <div className="min-h-screen">{children}</div>
      </main>
      <GuidedTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
  );
}
